/*
 * Purpose: Gmail inbound setup — webhook, OAuth API, Cloudflare deploy hints
 * Last modified: 2026-06-25 | Agent
 * Completeness: 90
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  checkWebhookServerHealth,
  createGmailInbox,
  defaultLinkedInJobFixture,
  sendTestGmailEvent,
} from '@/services/connectors/gmailInbound';
import {
  connectGmailOAuth,
  disconnectGmailOAuth,
  getGmailAccessToken,
  hasGoogleOAuthConfig,
} from '@/services/connectors/gmailOAuth';
import { PRODUCT_BRAND } from '@/lib/productTerms';
import { fetchLatestGmailApiMessage } from '@/services/connectors/gmailApi';
import { defaultGmailSourceForOutcome } from '@/services/connectors/gmailSource';
import { gmailWebhookPostUrl, isRemoteInboundApi } from '@/lib/apiBase';
import type { OutcomePlan } from '@/services/autoblocks/types';
import { Cloud, Copy, Link2, Mail, RefreshCw, Send, Unlink } from 'lucide-react';
import { ContextualError } from '@/components/errors/ContextualError';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { toast } from 'sonner';

interface GmailConnectorPanelProps {
  plan: OutcomePlan;
  onInboxReady?: (inboxId: string, postUrl: string) => void;
}

export function GmailConnectorPanel({ plan, onInboxReady }: GmailConnectorPanelProps) {
  const [serverUp, setServerUp] = useState<boolean | null>(null);
  const [inboxId, setInboxId] = useState(plan.gmailInboxId || '');
  const [postUrl, setPostUrl] = useState(plan.gmailPostUrl || '');
  const [busy, setBusy] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(Boolean(getGmailAccessToken()));
  const [gmailQuery, setGmailQuery] = useState('from:linkedin.com subject:job newer_than:7d');

  const refreshHealth = useCallback(async () => {
    setServerUp(await checkWebhookServerHealth());
    setGmailConnected(Boolean(getGmailAccessToken()));
  }, []);

  useEffect(() => {
    void refreshHealth();
  }, [refreshHealth]);

  const ensureInbox = async () => {
    setBusy(true);
    try {
      const inbox = await createGmailInbox();
      setInboxId(inbox.inboxId);
      setPostUrl(inbox.postUrl);
      onInboxReady?.(inbox.inboxId, inbox.postUrl);
      toast.success('Gmail inbox created');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create inbox');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!inboxId) {
      toast.error('Create an inbox first');
      return;
    }
    setBusy(true);
    try {
      await sendTestGmailEvent(inboxId, defaultLinkedInJobFixture());
      toast.success('Test email posted — Run workflow or dry run again');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setBusy(false);
    }
  };

  const displayPostUrl = postUrl || (inboxId ? gmailWebhookPostUrl(inboxId) : '');

  const copyUrl = () => {
    const path = displayPostUrl.startsWith('http')
      ? displayPostUrl
      : `${window.location.origin}${displayPostUrl.startsWith('/') ? '' : '/'}${displayPostUrl}`;
    void navigator.clipboard.writeText(path);
    toast.success('Webhook URL copied');
  };

  const onConnectGmail = async () => {
    setBusy(true);
    try {
      await connectGmailOAuth();
      setGmailConnected(true);
      toast.success('Gmail connected — enable Gmail API source on trigger node');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'OAuth failed');
    } finally {
      setBusy(false);
    }
  };

  const onTestGmailApi = async () => {
    setBusy(true);
    try {
      const msg = await fetchLatestGmailApiMessage(gmailQuery);
      if (!msg) toast.message('No messages matched query');
      else toast.success(`Fetched: ${msg.subject}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Gmail API failed');
    } finally {
      setBusy(false);
    }
  };

  const defaultTab = defaultGmailSourceForOutcome(plan.rawOutcome, plan.interpretedGoal);

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <Mail className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">Gmail connector</CardTitle>
          <Badge variant={serverUp ? 'default' : 'secondary'}>
            {serverUp === null ? '…' : serverUp ? 'Inbound API online' : 'Inbound API offline'}
          </Badge>
          {isRemoteInboundApi() && (
            <Badge variant="outline">
              <Cloud className="h-3 w-3 mr-1" />
              Cloud
            </Badge>
          )}
        </div>
        <CardDescription className="text-xs space-y-2">
          <span className="block">
            <strong>Your real Gmail (recommended):</strong> Connect Gmail → we search your existing
            inbox (e.g. LinkedIn job alerts). No separate mailbox.
          </span>
          <span className="block text-muted-foreground">
            <strong>Edge inbox (advanced):</strong> a synthetic webhook bucket — only if another tool
            POSTs parsed email JSON to {PRODUCT_BRAND}.
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={defaultTab === 'gmail_api' ? 'oauth' : 'webhook'} className="w-full">
          <TabsList className="w-full grid grid-cols-2 h-auto">
            <TabsTrigger value="oauth" className="text-xs">
              Your Gmail (OAuth)
            </TabsTrigger>
            <TabsTrigger value="webhook" className="text-xs">
              Edge inbox (webhook)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="webhook" className="space-y-3 mt-3 text-sm">
            <p className="text-xs text-muted-foreground rounded-md border border-border p-2">
              Not your Gmail inbox — a temporary bucket inside {PRODUCT_BRAND}. Something else must POST
              email JSON here (e.g. a Gmail filter + script). Skip this if you use OAuth above.
            </p>
            {!serverUp && !isRemoteInboundApi() && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Run <code className="text-[10px]">npm run dev</code> locally, or set{' '}
                <code className="text-[10px]">VITE_API_URL</code> to{' '}
                <code className="text-[10px]">https://api.autoblocks.run</code> (or optional edge Worker).
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => void refreshHealth()}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                Check API
              </Button>
              <Button size="sm" onClick={() => void ensureInbox()} disabled={!serverUp || busy}>
                Create inbox
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void sendTest()}
                disabled={!inboxId || busy}
              >
                <Send className="h-3.5 w-3.5 mr-1" />
                Test POST
              </Button>
            </div>
            {inboxId && (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Inbox ID</Label>
                  <Input readOnly value={inboxId} className="text-xs font-mono mt-1" />
                </div>
                <div>
                  <Label className="text-xs">POST URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input readOnly value={displayPostUrl} className="text-xs font-mono" />
                    <Button size="icon" variant="outline" onClick={copyUrl} aria-label="Copy URL">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="oauth" className="space-y-3 mt-3 text-sm">
            {!hasGoogleOAuthConfig() && (
              <ContextualError
                message="GOOGLE_CLIENT_ID is missing from your vault — add a Google OAuth Web client to connect Gmail."
                action={{ label: 'Add in Setup', href: REMEDIATION.setupGoogle }}
                secondaryAction={{ label: 'Builder Secrets', href: REMEDIATION.builderSecrets }}
              />
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => void onConnectGmail()} disabled={busy || !hasGoogleOAuthConfig()}>
                <Link2 className="h-3.5 w-3.5 mr-1" />
                {gmailConnected ? 'Reconnect Gmail' : 'Connect Gmail'}
              </Button>
              {gmailConnected && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    disconnectGmailOAuth();
                    setGmailConnected(false);
                    toast.success('Gmail disconnected');
                  }}
                >
                  <Unlink className="h-3.5 w-3.5 mr-1" />
                  Disconnect
                </Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => void onTestGmailApi()} disabled={!gmailConnected || busy}>
                Test fetch
              </Button>
            </div>
            <div>
              <Label className="text-xs">Gmail search query (for trigger node)</Label>
              <Input
                value={gmailQuery}
                onChange={(e) => setGmailQuery(e.target.value)}
                className="text-xs font-mono mt-1"
                placeholder="from:linkedin.com subject:job"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Uses your signed-in Google account. Job alerts stay in your normal inbox — the workflow
              query filters them (same as Gmail search). Token saved as GOOGLE_ACCESS_TOKEN in Secrets.
            </p>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
